import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import DataGrid, { GridColumn, GridAction } from '../../components/DataGrid/DataGrid';
import PermissionGuard from '../../components/PermissionGuard';
import { Permissions } from '../../constants/Permissions';
import { useAuth } from '../../context/AuthContext';
import departmentService from '../../services/departmentService';
import './DepartmentListPage.css';
import { CirclePlus, Eye, RefreshCw, SquarePen, Trash2 } from 'lucide-react';

const DepartmentListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadItems = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await departmentService.getDepartments(1, 100);
      if (response?.data) {
        setItems(response.data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('department.failedToLoadDepartment'));
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await departmentService.getDepartments(1, 100);
      if (response?.data) {
        setItems(response.data);
      }
      setRefreshing(false);
    } catch (err) {
      setError(t('department.failedToLoadDepartment'));
      setRefreshing(false);
    }
  };

  const columns: GridColumn[] = [
    {
      key: 'departmentName',
      label: t('department.departmentName'),
      sortable: true,
      width: '220px',
      render: (value) => <strong>{value}</strong>,
    },
    {
      key: 'departmentType',
      label: t('department.type'),
      sortable: true,
      width: '150px',
      render: (value) => (
        <span className="badge badge-active">{value}</span>
      ),
    },
    {
      key: 'description',
      label: t('common.description'),
      sortable: true,
      width: '280px',
      hideTablet: true,
      hideMobile: true,
      render: (value) => <span className="description-text">{value || 'N/A'}</span>,
    },
    {
      key: 'isActive',
      label: t('common.status'),
      width: '120px',
      render: (value) => <span className="badge badge-active">{value ? t('common.active') : t('common.inactive')}</span>,
    },
    {
      key: 'createdDate',
      label: t('common.createdDate'),
      sortable: true,
      width: '140px',
      hideMobile: true,
      render: (value) => new Date(value || new Date()).toLocaleDateString(t('common.languageCode') === 'ja' ? 'ja-JP' : 'en-US'),
    },
  ];

  const handleDelete = (id: number) => {
    navigate(`/organization/departments/${id}/delete`);
  };

  const actions: GridAction[] = [
    {
      icon: (
        <Eye size={16} strokeWidth={1.75} />
      ),
      onClick: (item) => navigate(`/organization/departments/${item.departmentId}`),
      className: 'grid-action-btn-view',
      title: t('common.view'),
    },
    ...(hasPermission(Permissions.Departments.Edit)
      ? [{
          icon: (
            <SquarePen size={16} strokeWidth={1.75} />
          ),
          onClick: (item: any) => navigate(`/organization/departments/${item.departmentId}/edit`),
          className: 'grid-action-btn-edit',
          title: t('common.edit'),
        }]
      : []),
    ...(hasPermission(Permissions.Departments.Delete)
      ? [{
          icon: (
            <Trash2 size={16} strokeWidth={1.75} />
          ),
          onClick: (item: any) => handleDelete(item.departmentId),
          className: 'grid-action-btn-delete',
          title: t('common.delete'),
        }]
      : []),
  ];

  return (
    <Layout>
      <div className="container">
        <div className="header">
          <h1 className="title">{t('department.title')}</h1>
          <div className="header-actions">
            <PermissionGuard claim={Permissions.Departments.Create}>
              <button
                className="btn-new"
                onClick={() => navigate('/organization/departments/create')}
                disabled={loading}
              >
                <CirclePlus/>
                {t('department.newDepartment')}
              </button>
            </PermissionGuard>
            <button
              className="btn-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              title={t('common.refresh')}
            >
              <RefreshCw size={18} strokeWidth={2} className={refreshing ? 'refresh-icon-spinning' : ''} />
              {refreshing ? t('common.refreshing') : t('common.refresh')}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
            ></button>
          </div>
        )}

        <DataGrid
          data={items}
          columns={columns}
          actions={actions}
          loading={loading}
          pageSize={10}
          searchPlaceholder={t('common.search')}
          emptyMessage={t('department.noDepartmentsFound')}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </div>
    </Layout>
  );
};

export default DepartmentListPage;
